import { useCallback, useRef, useState } from 'react';
import {
  clampPan,
  midpoint,
  MIN_ZOOM,
  pinchDistance,
  RESET_ZOOM,
  zoomAbout,
  type Point,
  type ZoomState,
} from './report-zoom';

/**
 * TWO FINGERS ON THE REPORT — M10 / S10-03B.
 *
 * **POINTER EVENTS, NOT A LIBRARY.** Pinch is two pointers and a ratio of
 * distances; pan is one pointer and a delta. The browser already reports both,
 * so a dependency would buy nothing and cost a decision Jerry has to make.
 *
 * **NO ZOOM BUTTONS.** Brandon pinches, the way he does everywhere else on his
 * phone. A plus and a minus button would be a different product pretending to
 * be this one.
 *
 * **THE GESTURE SURFACE IS THE REPORT ONLY.** The hook is attached to the pages,
 * never to the screen — Back, the title and the branding checkbox stay ordinary
 * Android size, and nothing here enables browser zoom for the application.
 *
 * **AND IT DECIDES NOTHING ABOUT THE DOCUMENT.** Page packing has already
 * happened at canonical Letter dimensions by the time a finger lands.
 */
export interface PinchZoom {
  readonly state: ZoomState;
  /**
   * True once Brandon has zoomed past fit-width.
   *
   * **THE SURFACE CHANGES CHARACTER ON THIS FLAG.** At fit-width the report is
   * ordinary page content and the browser owns vertical scrolling; zoomed in,
   * the surface becomes a window and owns the gesture. One finger, one owner —
   * letting both interpret the same drag is what made panning feel like
   * fighting something, because it was.
   */
  readonly isZoomed: boolean;
  readonly reset: () => void;
  readonly handlers: {
    readonly onPointerDown: (event: React.PointerEvent) => void;
    readonly onPointerMove: (event: React.PointerEvent) => void;
    readonly onPointerUp: (event: React.PointerEvent) => void;
    readonly onPointerCancel: (event: React.PointerEvent) => void;
  };
}

/**
 * The four numbers panning is bounded by, passed individually rather than as an
 * object so the handlers depend on plain values that cannot be rebuilt every
 * render by an unrelated piece of state.
 */
export interface PinchBounds {
  readonly contentWidth: number;
  readonly contentHeight: number;
  readonly viewportWidth: number;
  readonly viewportHeight: number;
}

interface Gesture {
  /** Live pointers, so a second finger arriving turns a drag into a pinch. */
  readonly pointers: Map<number, Point>;
  /** Finger separation when the pinch began, and the zoom it began from. */
  startDistance: number;
  startZoom: number;
  /** Where the single dragging finger was last seen. */
  lastPan: Point | null;
}

export function usePinchZoom(
  contentWidth: number,
  contentHeight: number,
  viewportWidth: number,
  viewportHeight: number,
): PinchZoom {
  const [state, setState] = useState<ZoomState>(RESET_ZOOM);
  /**
   * Live gesture bookkeeping — a REF, and deliberately so.
   *
   * It is written on every pointer move, which is exactly what a ref is for:
   * state would re-render on each frame of a pinch, and a value captured from
   * state may not be mutated at all. **Nothing here is ever read during
   * render** — only inside event handlers, which is the rule refs must follow.
   */
  const gestureRef = useRef<Gesture>({
    pointers: new Map<number, Point>(),
    startDistance: 0,
    startZoom: 1,
    lastPan: null,
  });

  const local = (event: React.PointerEvent): Point => {
    const box = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - box.left, y: event.clientY - box.top };
  };

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      const gesture = gestureRef.current;
      const point = local(event);
      gesture.pointers.set(event.pointerId, point);
      // **CAPTURED ONLY WHEN THE REPORT OWNS THE GESTURE.** Capturing at
      // fit-width would steal the finger the browser needs for ordinary
      // scrolling; capturing while zoomed is what keeps a drag continuous as it
      // crosses a photo or a page edge.
      if (state.zoom > MIN_ZOOM || gesture.pointers.size === 2) {
        event.currentTarget.setPointerCapture?.(event.pointerId);
      }

      if (gesture.pointers.size === 2) {
        const [a, b] = [...gesture.pointers.values()];
        if (a && b) {
          gesture.startDistance = pinchDistance(a, b);
          gesture.startZoom = state.zoom;
        }
        gesture.lastPan = null;
      } else if (gesture.pointers.size === 1) {
        gesture.lastPan = point;
      }
    },
    [state.zoom],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      const gesture = gestureRef.current;
      if (!gesture.pointers.has(event.pointerId)) return;
      const point = local(event);
      gesture.pointers.set(event.pointerId, point);

      if (gesture.pointers.size >= 2) {
        const [a, b] = [...gesture.pointers.values()];
        if (!a || !b || gesture.startDistance <= 0) return;
        // **CONTINUOUS, NOT STEPPED.** The ratio of finger separation now to
        // finger separation then, applied to the zoom the pinch started from.
        const ratio = pinchDistance(a, b) / gesture.startDistance;
        setState((current) =>
          zoomAbout(current, midpoint(a, b), gesture.startZoom * ratio, {
            contentWidth,
            contentHeight,
            viewportWidth,
            viewportHeight,
          }),
        );
        return;
      }

      const last = gesture.lastPan;
      // **AT FIT-WIDTH THE BROWSER SCROLLS AND THE REPORT DOES NOT MOVE.**
      // There is nothing to pan to, and competing for the finger is exactly
      // what made scrolling feel sticky.
      if (!last || state.zoom <= MIN_ZOOM) return;
      gesture.lastPan = point;
      setState((current) => ({
        zoom: current.zoom,
        pan: clampPan(
          { x: current.pan.x + (point.x - last.x), y: current.pan.y + (point.y - last.y) },
          { zoom: current.zoom, contentWidth, contentHeight, viewportWidth, viewportHeight },
        ),
      }));
    },
    [contentWidth, contentHeight, viewportWidth, viewportHeight, state.zoom],
  );

  const release = useCallback((event: React.PointerEvent) => {
    const gesture = gestureRef.current;
    // **CAPTURE MUST BE RELEASED, AND IT WAS NOT.** A surface still holding a
    // finished pointer keeps swallowing events, which is felt as a drag that
    // stops responding for no visible reason.
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    gesture.pointers.delete(event.pointerId);
    gesture.lastPan =
      gesture.pointers.size === 1 ? ([...gesture.pointers.values()][0] ?? null) : null;
    if (gesture.pointers.size < 2) gesture.startDistance = 0;
  }, []);

  return {
    state,
    isZoomed: state.zoom > MIN_ZOOM,
    reset: useCallback(() => {
      setState(RESET_ZOOM);
    }, []),
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: release,
      onPointerCancel: release,
    },
  };
}
